/**
 * Dashboard Service
 * Handles dashboard metrics and statistics API calls
 */
import BaseApiService from '../base/BaseApiService';
import type {
  User,
  Assessment,
  AssessmentsResponse,
  SuppliersResponse,
  ContractsResponse,
  GapItem,
  EvidenceStatistics,
  ScheduleItemsResponse,
  ScheduleItem,
  ExecutiveDashboardData,
  ContractManagerDashboardResponse,
  ProgramLeadMetricsData,
  ProgramLeadQuickActionsData
} from '@app-types';
import { ROLES } from '@app-types';
import { hasRole, canUserAccessAssessments } from '@utils/permissionUtils';

class DashboardService extends BaseApiService {
  /**
   * Get dashboard metrics - Optimized to make only 3 API calls instead of 4
   * @param user - The authenticated user
   */
  async getDashboardMetrics(user: User): Promise<{
    assessments: { total: number; byStatus: Array<{ status: string; count: number }> };
    suppliers: { total: number };
    contracts: { total: number };
    recentAssessments: Assessment[];
    schedulerMetrics?: {
      readyForScheduling: number;
      scheduled: number;
      pendingInvitations: number;
      byStatus: Array<{ status: string; count: number }>;
    };
  }> {
    try {
      // Check if user can access assessments using permission utility
      const canAccess = canUserAccessAssessments(user);

      // Fetch data based on permissions
      let assessmentsResponse: AssessmentsResponse;
      let suppliersResponse: SuppliersResponse;
      let contractsResponse: ContractsResponse;

      if (canAccess) {
        // Fetch all data including assessments
        const assessmentsParams = this.buildQueryParams({ limit: 1000, sortBy: 'updated_at', order: 'DESC' });
        const suppliersParams = this.buildQueryParams({ limit: 1 });
        const contractsParams = this.buildQueryParams({ limit: 1, status: 'Active', assessment_scope: 'Include' });

        [assessmentsResponse, suppliersResponse, contractsResponse] = await Promise.all([
          this.get<AssessmentsResponse>(`/assessments?${assessmentsParams}`),
          this.get<SuppliersResponse>(`/suppliers?${suppliersParams}`),
          this.get<ContractsResponse>(`/contracts?${contractsParams}`)
        ]);
      } else {
        // Only fetch suppliers and contracts for Scheduler
        const suppliersParams = this.buildQueryParams({ limit: 1 });
        const contractsParams = this.buildQueryParams({ limit: 1, status: 'Active', assessment_scope: 'Include' });

        [suppliersResponse, contractsResponse] = await Promise.all([
          this.get<SuppliersResponse>(`/suppliers?${suppliersParams}`),
          this.get<ContractsResponse>(`/contracts?${contractsParams}`)
        ]);
        // Create empty assessments response for Scheduler
        assessmentsResponse = {
          data: [],
          total: 0,
          count: 0,
          statusTotals: {}
        };
      }

      // Calculate assessment statistics from the single assessments call
      const assessmentsByStatus: { [key: string]: number } = {};
      assessmentsResponse.data.forEach((assessment) => {
        const status = assessment.assessment_status;
        assessmentsByStatus[status] = (assessmentsByStatus[status] || 0) + 1;
      });

      const statusArray = Object.entries(assessmentsByStatus).map(([status, count]) => ({
        status,
        count
      }));

      // Get recent assessments from the same data (first 10 items)
      const recentAssessments = assessmentsResponse.data.slice(0, 10);

      // Calculate scheduler-specific metrics if user is Scheduler
      let schedulerMetrics;
      if (hasRole(user, ROLES.SCHEDULER)) {
        // Fetch totals for ALL schedule items (for the chart)
        const allItemsParams = this.buildQueryParams({ limit: 1, viewMode: 'scheduler' });
        const allItemsResponse = await this.get<ScheduleItemsResponse>(
          `/contract-assessment-meetings?${allItemsParams}`
        );
        const allStatusTotals = allItemsResponse.statusTotals;

        // Fetch MY schedule items (for metric cards)
        const myItemsParams = this.buildQueryParams({
          limit: 100,
          processedByMe: true,
          viewMode: 'scheduler'
        });
        const myItemsResponse = await this.get<ScheduleItemsResponse>(`/contract-assessment-meetings?${myItemsParams}`);
        const myScheduleItems = myItemsResponse.data;
        const myStatusTotals = myItemsResponse.statusTotals;

        // Use statusTotals for my metrics (for metric cards)
        const readyForScheduling = myStatusTotals.ready_for_scheduling || 0;
        const scheduled = myStatusTotals.scheduled || 0;

        // Count MY pending invitations (meetings with 'sent' status)
        const pendingInvitations = myScheduleItems.filter((item: ScheduleItem) =>
          item.meeting?.invites?.some((invite) => invite.status === 'sent')
        ).length;

        // Use ALL statusTotals for the chart
        const byStatus = [
          { status: 'pending_data_enrichment', count: allStatusTotals.pending_data_enrichment || 0 },
          { status: 'ready_for_scheduling', count: allStatusTotals.ready_for_scheduling || 0 },
          { status: 'scheduled', count: allStatusTotals.scheduled || 0 }
        ];

        schedulerMetrics = {
          readyForScheduling,
          scheduled,
          pendingInvitations,
          byStatus
        };
      }

      return {
        assessments: {
          total: assessmentsResponse.total,
          byStatus: statusArray
        },
        suppliers: {
          total: suppliersResponse.total
        },
        contracts: {
          total: contractsResponse.total
        },
        recentAssessments: recentAssessments,
        schedulerMetrics
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  }

  /**
   * Get Contract Role dashboard data
   */
  async getContractManagerDashboard(): Promise<ContractManagerDashboardResponse> {
    return this.get<ContractManagerDashboardResponse>('/contract-manager/dashboard');
  }

  /**
   * Get Contract Role gap items
   */
  async getContractManagerGapItems(): Promise<{
    contract_manager_id: number;
    total_gap_items: number;
    items_needing_evidence: number;
    gap_items: GapItem[];
    items_requiring_upload: GapItem[];
    evidence_statistics: EvidenceStatistics;
  }> {
    return this.get<{
      contract_manager_id: number;
      total_gap_items: number;
      items_needing_evidence: number;
      gap_items: GapItem[];
      items_requiring_upload: GapItem[];
      evidence_statistics: EvidenceStatistics;
    }>('/contract-manager/gap-items');
  }

  /**
   * Get Executive dashboard data
   */
  async getExecutiveDashboard(): Promise<ExecutiveDashboardData> {
    return this.get<ExecutiveDashboardData>('/executive/dashboard');
  }

  async getProgramLeadMetrics(): Promise<ProgramLeadMetricsData> {
    return this.get<ProgramLeadMetricsData>('/program-lead/metrics');
  }

  async getProgramLeadQuickActions(): Promise<ProgramLeadQuickActionsData> {
    return this.get<ProgramLeadQuickActionsData>('/program-lead/quick-actions');
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;
